/**
 * Usage caps — shared React infrastructure for the Feb 2026 fair-use pricing.
 *
 * The backend at `/api/usage/me` reports per-kind usage against the plan cap.
 * We expose:
 *   - useUsage()      hook that fetches + auto-refreshes on window focus
 *   - <UsageMeter />  3-bar visual meter for the Settings page
 *   - <UsageBanner /> soft-warn banner shown when any kind crosses 80%
 *   - <UsageCapModal /> hard-block modal shown when a 429 comes back
 *   - handleUsageCapError(err, { onUpgrade })  centralised 429 handler
 *
 * The pattern: any endpoint that increments a cap can return HTTP 429 with
 * `detail: { code: 'usage_cap_hit', kind, current, limit, plan, message }`.
 * Callers wrap their fetch in try/catch and pipe errors through
 * `handleUsageCapError` to pop the modal — no per-feature bespoke messaging.
 */
import React, { useState, useEffect, useCallback, createContext, useContext } from "react";
import axios from "axios";
import { AlertTriangle, Zap, Sparkles, X, TrendingUp } from "lucide-react";
import { Button } from "./components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Human-readable labels per metered kind — matches backend `usage_counters` keys.
export const KIND_META = {
  ask_ai_msgs: { label: "Ask AI messages", icon: Sparkles, unit: "messages" },
  competitor_analyses: { label: "Competitor hook analyses", icon: Zap, unit: "videos" },
  blend_hooks: { label: "Blend Hooks generations", icon: TrendingUp, unit: "blends" },
};

// ─── Shared context ─────────────────────────────────────────────────────────
// Provides `usage`, `refresh`, and the cap-hit modal state to any child. Wraps
// the entire Dashboard so the meter (Settings) and the auto-modal (429 handler
// anywhere) share a single fetch cycle.
const UsageContext = createContext(null);

export function UsageProvider({ children }) {
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [capHit, setCapHit] = useState(null); // { kind, current, limit, plan, message } or null

  const refresh = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/usage/me`);
      setUsage(data);
    } catch (e) {
      // Silent — usage meter is cosmetic. 401 during logout is expected.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    // Refresh when the tab regains focus so the meter is fresh after a long AFK.
    const onFocus = () => refresh();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [refresh]);

  // Global 429 interceptor — any endpoint that emits `usage_cap_hit` pops the
  // modal without the calling component needing bespoke error handling. The
  // interceptor RE-THROWS so callers still see the failure and can suppress
  // their own toast; `handleUsageCapError` exposes the same detection for any
  // caller that wants to skip its own error toast.
  useEffect(() => {
    const id = axios.interceptors.response.use(
      (r) => r,
      (err) => {
        const detail = err?.response?.data?.detail;
        if (err?.response?.status === 429 && detail && typeof detail === "object" && detail.code === "usage_cap_hit") {
          setCapHit(detail);
          refresh();
        }
        return Promise.reject(err);
      },
    );
    return () => axios.interceptors.response.eject(id);
  }, [refresh]);

  const showCapHit = useCallback((info) => setCapHit(info), []);
  const closeCapHit = useCallback(() => setCapHit(null), []);

  return (
    <UsageContext.Provider value={{ usage, loading, refresh, capHit, showCapHit, closeCapHit }}>
      {children}
    </UsageContext.Provider>
  );
}

export function useUsage() {
  const ctx = useContext(UsageContext);
  if (!ctx) throw new Error("useUsage must be used inside <UsageProvider>");
  return ctx;
}

// ─── 429 error piping ───────────────────────────────────────────────────────
/** Parse an axios error and, if it's a usage_cap_hit, trigger the modal.
 *  Returns true if the error was handled (caller can suppress its own toast). */
export function handleUsageCapError(err, { showCapHit, refresh }) {
  const status = err?.response?.status;
  const detail = err?.response?.data?.detail;
  if (status === 429 && detail && typeof detail === "object" && detail.code === "usage_cap_hit") {
    showCapHit(detail);
    // Also refresh so the meter shows 100% immediately.
    refresh?.();
    return true;
  }
  return false;
}

// ─── UI: 3-bar meter for Settings ───────────────────────────────────────────
export function UsageMeter({ usage }) {
  if (!usage) return null;
  const kinds = Object.keys(usage.usage || {});
  return (
    <div className="space-y-4" data-testid="usage-meter">
      <div className="flex items-baseline justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 font-semibold">This month</div>
          <div className="text-white text-lg font-semibold" data-testid="usage-meter-plan">
            {usage.plan_name} plan · {usage.month_key}
          </div>
        </div>
      </div>
      {kinds.map((kind) => {
        const meta = KIND_META[kind] || { label: kind, unit: "uses" };
        const row = usage.usage[kind];
        const barColor = row.pct_used >= 100 ? "bg-red-500" : row.pct_used >= 80 ? "bg-amber-500" : "bg-emerald-500";
        return (
          <div key={kind} data-testid={`usage-meter-row-${kind}`}>
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-2">
                <meta.icon className="w-3.5 h-3.5 text-zinc-500" />
                <span className="text-sm text-zinc-300">{meta.label}</span>
              </div>
              <div className="text-xs text-zinc-400 tabular-nums">
                {row.unlimited ? (
                  <span className="text-emerald-400 font-medium">Unlimited</span>
                ) : (
                  <>
                    <span className="text-white font-medium" data-testid={`usage-meter-${kind}-current`}>{row.current}</span>
                    <span className="text-zinc-500"> / {row.limit}</span>
                  </>
                )}
              </div>
            </div>
            {!row.unlimited && (
              <div className="h-1.5 rounded-full bg-white/[0.05] overflow-hidden">
                <div
                  className={`h-full ${barColor} transition-all duration-500`}
                  style={{ width: `${Math.min(100, row.pct_used)}%` }}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── UI: soft-warn banner (dashboard-wide, dismissible) ─────────────────────
/** Renders a slim amber banner at the top of the dashboard when any metered
 *  kind is between 80% and 100%. Dismiss lives in localStorage keyed by
 *  (month, kind) so dismissing this month's warning doesn't hide next month's. */
export function UsageBanner({ onUpgrade }) {
  const { usage } = useUsage();
  const [dismissed, setDismissed] = useState(false);

  const warnKind = (() => {
    if (!usage?.usage) return null;
    for (const [kind, row] of Object.entries(usage.usage)) {
      if (!row.unlimited && row.pct_used >= 80 && row.pct_used < 100) return { kind, row };
    }
    return null;
  })();

  const dismissKey = warnKind ? `usage-warn-dismissed:${usage.month_key}:${warnKind.kind}` : null;

  useEffect(() => {
    if (dismissKey) {
      setDismissed(localStorage.getItem(dismissKey) === "1");
    } else {
      setDismissed(false);
    }
  }, [dismissKey]);

  if (!warnKind || dismissed) return null;

  const meta = KIND_META[warnKind.kind] || { label: warnKind.kind };
  const { current, limit, pct_used } = warnKind.row;
  const remaining = Math.max(0, limit - current);

  return (
    <div
      className="mx-6 sm:mx-10 mt-6 mb-2 rounded-xl border border-amber-500/25 bg-amber-500/[0.06] p-4 flex items-start gap-3 backdrop-blur-md"
      data-testid={`usage-warn-banner-${warnKind.kind}`}
    >
      <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="text-sm text-amber-100 font-medium">
          You&rsquo;ve used {Math.round(pct_used)}% of this month&rsquo;s {meta.label.toLowerCase()} —{" "}
          <span className="text-amber-200">{remaining} left</span>.
        </div>
        <div className="text-xs text-amber-200/70 mt-0.5">
          Upgrade to Pro for unlimited hook analyses and blends. Ask AI included.
        </div>
      </div>
      <div className="flex items-center gap-1.5 flex-shrink-0">
        <Button
          size="sm"
          onClick={onUpgrade}
          className="bg-amber-500 hover:bg-amber-400 text-amber-950 border border-amber-300/40 h-8 font-medium"
          data-testid="usage-warn-upgrade-btn"
        >
          Upgrade
        </Button>
        <button
          type="button"
          onClick={() => { localStorage.setItem(dismissKey, "1"); setDismissed(true); }}
          className="p-1 rounded-md text-amber-300/60 hover:text-amber-100 hover:bg-amber-500/10 transition-colors"
          aria-label="Dismiss"
          data-testid="usage-warn-dismiss-btn"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

// ─── UI: hard-block modal (fires on 429 usage_cap_hit) ──────────────────────
export function UsageCapModal({ onUpgrade }) {
  const { capHit, closeCapHit } = useUsage();
  if (!capHit) return null;
  const meta = KIND_META[capHit.kind] || { label: capHit.kind };
  const isPro = capHit.plan === "pro";
  return (
    <Dialog open={!!capHit} onOpenChange={(o) => !o && closeCapHit()}>
      <DialogContent className="sm:max-w-md bg-zinc-950 border border-white/10 text-zinc-100" data-testid="usage-cap-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white">
            <Zap className="w-5 h-5 text-amber-400" />
            You&rsquo;ve hit this month&rsquo;s limit
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            {capHit.message || `You've used your ${capHit.limit} monthly ${meta.label.toLowerCase()}.`}
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-xl border border-red-500/20 bg-red-500/[0.05] p-4">
          <div className="flex items-center justify-between mb-1">
            <div className="text-sm text-zinc-300 font-medium">{meta.label}</div>
            <div className="text-sm text-white tabular-nums font-semibold" data-testid="usage-cap-modal-count">
              {capHit.current} / {capHit.limit}
            </div>
          </div>
          <div className="h-1.5 rounded-full bg-white/[0.05] overflow-hidden">
            <div className="h-full bg-red-500 w-full" />
          </div>
        </div>

        {!isPro && (
          <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/[0.04] p-4 space-y-1.5">
            <div className="text-sm font-medium text-emerald-300 flex items-center gap-1.5">
              <Sparkles className="w-4 h-4" /> Pro — $79/mo
            </div>
            <ul className="text-xs text-zinc-300 space-y-1 mt-2 ml-1">
              <li>· Unlimited hook analyses + blends</li>
              <li>· Ask AI upgraded to Claude Sonnet 4.5</li>
              <li>· 1,000 Ask AI messages / mo (5× Starter)</li>
            </ul>
          </div>
        )}

        <div className="flex items-center gap-2 pt-1">
          <Button
            variant="outline"
            onClick={closeCapHit}
            className="flex-1 bg-white/[0.03] border-white/10 text-zinc-300 hover:bg-white/[0.08] hover:text-white"
            data-testid="usage-cap-modal-close"
          >
            Not now
          </Button>
          {!isPro && (
            <Button
              onClick={() => { closeCapHit(); onUpgrade?.(); }}
              className="flex-1 bg-red-600 hover:bg-red-500 text-white border border-red-400/30 shadow-[0_0_18px_rgba(255,0,0,0.3)]"
              data-testid="usage-cap-modal-upgrade"
            >
              Upgrade to Pro
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
